/**
 * Hand-drawn circuits — the workshop's freehand editor format (owner request,
 * 2026-07-18).
 *
 * A design is a CLOSED loop of cubic Bézier segments. Each anchor carries its
 * position, two handle offsets (in/out, relative to the anchor so moving an
 * anchor drags its handles along), the full corridor width at that anchor and
 * the surface of the segment STARTING at it — same convention as the generated
 * circuits' control points. `startOffset` places the start/finish line along
 * the resampled centerline.
 *
 * `buildTrackFromDesign` turns a design into the exact same `Track` structure
 * as generated circuits: everything downstream (physics, walls, checkpoints,
 * server timing, records, grid) works unchanged. The build is pure arithmetic
 * over the stored JSON numbers, so the client preview, the client in-race
 * rebuild and the server authority all produce bit-identical tracks.
 */

import { lerp } from './math';
import type { SurfaceId } from './physics';
import type { RawCenterPoint, Track } from './track';
import { assembleTrackFromRaw } from './track';
import type { TrackIssue } from './validation';

export interface DesignAnchor {
  x: number;
  y: number;
  /** Handle of the INCOMING segment, as an offset from the anchor. */
  inX: number;
  inY: number;
  /** Handle of the OUTGOING segment, as an offset from the anchor. */
  outX: number;
  outY: number;
  /** Full corridor width at this anchor, px. */
  width: number;
  /** Surface of the segment STARTING at this anchor. */
  surface: SurfaceId;
  /**
   * Editor hint: this anchor's handles follow its neighbours automatically
   * (smooth point). Ignored by the builder — the stored offsets are always
   * authoritative, so the build never depends on editor logic.
   */
  auto?: boolean;
}

export interface TrackDesign {
  version: 1;
  /** Closed loop: segment i joins anchor i to anchor (i+1) % n. */
  anchors: DesignAnchor[];
  /** Start/finish line, px along the resampled centerline from anchor 0. */
  startOffset: number;
}

/**
 * Authoritative bounds — shared by the editor UI and the zod network schema.
 * CAUTION: the editor must make it impossible to author values outside of
 * them (input clamping) and `roundDesign` re-clamps on export — a design that
 * violated the schema was silently dropped by the server (`parseClientMessage`
 * treats it as garbage), which read as "Enregistrer ne répond pas" in the
 * owner's playtest. Handles are wide (±4000) because auto-handles scale with
 * anchor spacing and hand-pulled handles can legitimately be long.
 */
export const DESIGN_BOUNDS = {
  anchors: { min: 3, max: 64 },
  coord: { min: -20000, max: 20000 },
  handle: { min: -4000, max: 4000 },
  /** Same corridor bounds as the parametric generator. */
  width: { min: 90, max: 240 },
  startOffset: { min: 0, max: 100000 },
  /**
   * Accepted centerline length for a drawn circuit (px). The max is bounded by
   * the O(n²) clearance validation (~4000 samples ≈ 50 ms, run debounced) —
   * benchmarked fine at 25000 (owner request 2026-07-18; was 16000).
   */
  length: { min: 1500, max: 25000 },
} as const;

/** Validation profile for drawn circuits: geometry rules + length bounds. */
export const DESIGN_VALIDATION = {
  minArcSeparation: 250,
  clearanceMargin: 30,
  minWidth: 80,
  curvatureMargin: 12,
  minLength: DESIGN_BOUNDS.length.min,
  maxLength: DESIGN_BOUNDS.length.max,
} as const;

/** Raw sampling target spacing along each Bézier segment (px). */
const RAW_TARGET_SPACING = 4;
const RAW_MIN_PER_SEGMENT = 16;
const RAW_MAX_PER_SEGMENT = 512;

function cubic(p0: number, p1: number, p2: number, p3: number, u: number): number {
  const v = 1 - u;
  return v * v * v * p0 + 3 * v * v * u * p1 + 3 * v * u * u * p2 + u * u * u * p3;
}

/** The four control points of design segment `i` (absolute coordinates). */
export function segmentControls(
  design: TrackDesign,
  i: number,
): {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  x3: number;
  y3: number;
} {
  const n = design.anchors.length;
  const a = design.anchors[i] as DesignAnchor;
  const b = design.anchors[(i + 1) % n] as DesignAnchor;
  return {
    x0: a.x,
    y0: a.y,
    x1: a.x + a.outX,
    y1: a.y + a.outY,
    x2: b.x + b.inX,
    y2: b.y + b.inY,
    x3: b.x,
    y3: b.y,
  };
}

function smoothstep01(t: number): number {
  const c = t < 0 ? 0 : t > 1 ? 1 : t;
  return c * c * (3 - 2 * c);
}

/**
 * Deterministic raw-sample count for one segment: proportional to the control
 * polygon length (an upper bound of the arc length), bounded. Pure arithmetic
 * over stored numbers — identical on every JS engine.
 */
function rawCountFor(c: ReturnType<typeof segmentControls>): number {
  const d01 = Math.sqrt((c.x1 - c.x0) ** 2 + (c.y1 - c.y0) ** 2);
  const d12 = Math.sqrt((c.x2 - c.x1) ** 2 + (c.y2 - c.y1) ** 2);
  const d23 = Math.sqrt((c.x3 - c.x2) ** 2 + (c.y3 - c.y2) ** 2);
  const poly = d01 + d12 + d23;
  return Math.min(
    Math.max(Math.ceil(poly / RAW_TARGET_SPACING), RAW_MIN_PER_SEGMENT),
    RAW_MAX_PER_SEGMENT,
  );
}

/**
 * Builds the runnable track of a design. Throws on structurally impossible
 * input (too few anchors, non-finite numbers); geometric quality (clearance,
 * curvature, width, length) is judged separately with
 * `validateTrack(track, DESIGN_VALIDATION)`.
 */
export function buildTrackFromDesign(design: TrackDesign): Track {
  const n = design.anchors.length;
  if (n < DESIGN_BOUNDS.anchors.min) {
    throw new Error(`buildTrackFromDesign: need at least ${DESIGN_BOUNDS.anchors.min} anchors`);
  }
  for (const a of design.anchors) {
    if (
      !Number.isFinite(a.x) ||
      !Number.isFinite(a.y) ||
      !Number.isFinite(a.inX) ||
      !Number.isFinite(a.inY) ||
      !Number.isFinite(a.outX) ||
      !Number.isFinite(a.outY) ||
      !Number.isFinite(a.width)
    ) {
      throw new Error('buildTrackFromDesign: non-finite anchor');
    }
  }
  if (!Number.isFinite(design.startOffset)) {
    throw new Error('buildTrackFromDesign: non-finite startOffset');
  }

  const raw: RawCenterPoint[] = [];
  for (let i = 0; i < n; i++) {
    const a = design.anchors[i] as DesignAnchor;
    const b = design.anchors[(i + 1) % n] as DesignAnchor;
    const c = segmentControls(design, i);
    const count = rawCountFor(c);
    for (let k = 0; k < count; k++) {
      const u = k / count;
      raw.push({
        x: cubic(c.x0, c.x1, c.x2, c.x3, u),
        y: cubic(c.y0, c.y1, c.y2, c.y3, u),
        halfWidth: lerp(a.width, b.width, smoothstep01(u)) / 2,
        surface: a.surface,
      });
    }
  }

  return assembleTrackFromRaw(raw, design.startOffset);
}

/**
 * Catmull-Rom-style automatic handles for anchor `i`: tangent along the
 * prev→next chord, each handle a third of the distance to its neighbour (which
 * reproduces near-circular arcs on regular polygons). Mutates the anchor.
 */
export function applyAutoHandles(anchors: DesignAnchor[], i: number): void {
  const n = anchors.length;
  const self = anchors[i] as DesignAnchor;
  if (n < 3) {
    self.inX = 0;
    self.inY = 0;
    self.outX = 0;
    self.outY = 0;
    return;
  }
  const prev = anchors[(i - 1 + n) % n] as DesignAnchor;
  const next = anchors[(i + 1) % n] as DesignAnchor;
  const dx = next.x - prev.x;
  const dy = next.y - prev.y;
  const len = Math.sqrt(dx * dx + dy * dy);
  if (len < 1e-6) {
    self.inX = 0;
    self.inY = 0;
    self.outX = 0;
    self.outY = 0;
    return;
  }
  const tx = dx / len;
  const ty = dy / len;
  const dNext = Math.sqrt((next.x - self.x) ** 2 + (next.y - self.y) ** 2);
  const dPrev = Math.sqrt((self.x - prev.x) ** 2 + (self.y - prev.y) ** 2);
  self.outX = clampHandle((tx * dNext) / 3);
  self.outY = clampHandle((ty * dNext) / 3);
  self.inX = clampHandle((-tx * dPrev) / 3);
  self.inY = clampHandle((-ty * dPrev) / 3);
}

/** Clamps one handle component into the network-schema bounds. */
export function clampHandle(v: number): number {
  return Math.min(Math.max(v, DESIGN_BOUNDS.handle.min), DESIGN_BOUNDS.handle.max);
}

/** Clamps one coordinate into the network-schema bounds. */
export function clampCoord(v: number): number {
  return Math.min(Math.max(v, DESIGN_BOUNDS.coord.min), DESIGN_BOUNDS.coord.max);
}

/** Recomputes the handles of every `auto` anchor (editor helper). */
export function refreshAutoHandles(anchors: DesignAnchor[]): void {
  for (let i = 0; i < anchors.length; i++) {
    if ((anchors[i] as DesignAnchor).auto) applyAutoHandles(anchors, i);
  }
}

/**
 * Reverses the driving direction. Anchor 0 stays first; handles are swapped;
 * each geometric portion keeps its surface and widths. `length` (of the built
 * track) maps the start line onto the same physical spot.
 */
export function reverseDesign(design: TrackDesign, length: number): TrackDesign {
  const anchors = design.anchors;
  const n = anchors.length;
  const out: DesignAnchor[] = [];
  for (let k = 0; k < n; k++) {
    const src = anchors[(n - k) % n] as DesignAnchor;
    const surfaceSrc = anchors[(n - k - 1 + n) % n] as DesignAnchor;
    out.push({
      x: src.x,
      y: src.y,
      inX: src.outX,
      inY: src.outY,
      outX: src.inX,
      outY: src.inY,
      width: src.width,
      surface: surfaceSrc.surface,
      auto: src.auto,
    });
  }
  let startOffset = length > 0 ? (length - design.startOffset) % length : 0;
  if (startOffset < 0) startOffset += length;
  return { version: 1, anchors: out, startOffset };
}

/**
 * Splits segment `segIndex` at parameter `u` (de Casteljau): inserts an anchor
 * exactly on the curve without changing the shape. The three touched anchors
 * lose their `auto` flag (their handles are now exact).
 */
export function insertAnchorInDesign(
  design: TrackDesign,
  segIndex: number,
  u: number,
): TrackDesign {
  const n = design.anchors.length;
  const i = ((segIndex % n) + n) % n;
  const j = (i + 1) % n;
  const a = design.anchors[i] as DesignAnchor;
  const b = design.anchors[j] as DesignAnchor;
  const c = segmentControls(design, i);
  const t = Math.min(Math.max(u, 0.02), 0.98);

  const l = (p: number, q: number): number => p + (q - p) * t;
  const q1x = l(c.x0, c.x1);
  const q1y = l(c.y0, c.y1);
  const rx = l(c.x1, c.x2);
  const ry = l(c.y1, c.y2);
  const sx = l(c.x2, c.x3);
  const sy = l(c.y2, c.y3);
  const q2x = l(q1x, rx);
  const q2y = l(q1y, ry);
  const t2x = l(rx, sx);
  const t2y = l(ry, sy);
  const mx = l(q2x, t2x);
  const my = l(q2y, t2y);

  const mid: DesignAnchor = {
    x: mx,
    y: my,
    inX: q2x - mx,
    inY: q2y - my,
    outX: t2x - mx,
    outY: t2y - my,
    width: lerp(a.width, b.width, smoothstep01(t)),
    surface: a.surface,
    auto: false,
  };
  const newA: DesignAnchor = { ...a, outX: q1x - a.x, outY: q1y - a.y, auto: false };
  const newB: DesignAnchor = { ...b, inX: sx - b.x, inY: sy - b.y, auto: false };

  const anchors = design.anchors.slice();
  anchors[i] = newA;
  anchors[j] = newB;
  anchors.splice(i + 1, 0, mid);
  return { version: 1, anchors, startOffset: design.startOffset };
}

/**
 * Starter design: a rounded oval the player can immediately reshape — nobody
 * stares at a blank canvas. All-asphalt, medium width, start on the bottom
 * straight.
 */
export function ovalTemplateDesign(): TrackDesign {
  const cx = 1400;
  const cy = 950;
  const rx = 1050;
  const ry = 620;
  const count = 8;
  const anchors: DesignAnchor[] = [];
  for (let k = 0; k < count; k++) {
    const angle = (k / count) * 2 * Math.PI + Math.PI / 2;
    anchors.push({
      x: Math.round(cx + Math.cos(angle) * rx),
      y: Math.round(cy + Math.sin(angle) * ry),
      inX: 0,
      inY: 0,
      outX: 0,
      outY: 0,
      width: 160,
      surface: 'asphalt',
      auto: true,
    });
  }
  for (let k = 0; k < count; k++) applyAutoHandles(anchors, k);
  const design: TrackDesign = { version: 1, anchors, startOffset: 0 };
  return roundDesign(design);
}

/**
 * Rounds every stored number to 0.1 px — compact JSON payloads, and what is
 * test-driven is bit-for-bit what is saved and replayed (both sides build from
 * these exact rounded values).
 */
export function roundDesign(design: TrackDesign): TrackDesign {
  // `|| 0` folds -0 into +0: JSON.stringify writes -0 as "0", and the builds
  // MUST run on the exact numbers that travel the wire. Every value is also
  // clamped into the schema bounds: what leaves this function can NEVER be
  // silently dropped by the server's zod gate.
  const r = (v: number): number => Math.round(v * 10) / 10 || 0;
  return {
    version: 1,
    anchors: design.anchors.map((a) => ({
      x: clampCoord(r(a.x)),
      y: clampCoord(r(a.y)),
      inX: clampHandle(r(a.inX)),
      inY: clampHandle(r(a.inY)),
      outX: clampHandle(r(a.outX)),
      outY: clampHandle(r(a.outY)),
      width: Math.round(
        Math.min(Math.max(a.width, DESIGN_BOUNDS.width.min), DESIGN_BOUNDS.width.max),
      ),
      surface: a.surface,
      ...(a.auto !== undefined ? { auto: a.auto } : {}),
    })),
    startOffset: Math.min(
      Math.max(r(design.startOffset), DESIGN_BOUNDS.startOffset.min),
      DESIGN_BOUNDS.startOffset.max,
    ),
  };
}

/** Player-facing French label of a validation issue (editor chip + server toast). */
export function designIssueLabelFr(issue: TrackIssue): string {
  const at = issue.s !== undefined ? ` (à ${Math.round(issue.s)} px du départ)` : '';
  switch (issue.code) {
    case 'narrow':
      return `Couloir trop étroit${at}`;
    case 'curvature':
      // A hairpin is legal as long as the corridor is narrow enough there: the
      // physical limit is turn radius > half width (inner edge folding).
      return (
        `Virage trop serré pour la largeur de la piste${at} — ` +
        `resserrez la largeur à cet endroit (mode 📏) ou ouvrez un peu la courbe`
      );
    case 'clearance':
      return (
        `Deux portions de piste se chevauchent${at} — ` + `écartez-les ou réduisez leurs largeurs`
      );
    case 'length':
      return `Longueur hors limites (${DESIGN_BOUNDS.length.min}–${DESIGN_BOUNDS.length.max} px)`;
    case 'samples':
      return 'Tracé impossible à construire — annulez la dernière action (Ctrl+Z)';
    default:
      return 'Géométrie invalide';
  }
}

/**
 * Non-blocking authoring warnings, beyond hard validity: a very twisty grid
 * zone makes staggered starts scrappy — worth a heads-up, not a rejection.
 */
export function designWarnings(track: Track): string[] {
  const warnings: string[] = [];
  // Inspect the zone the grid occupies (up to 8 cars → 4 rows ≈ 170 px + margin).
  const gridZone = 220;
  let minRadius = Infinity;
  const count = track.samples.length;
  for (let back = 0; back < gridZone; back += track.step) {
    let s = track.length - back;
    if (s < 0) s += track.length;
    const i = Math.round(s / track.step) % count;
    const a = track.samples[i]!;
    const b = track.samples[(i + 1) % count]!;
    const cross = a.tx * b.ty - a.ty * b.tx;
    const dot = a.tx * b.tx + a.ty * b.ty;
    const dTheta = Math.abs(Math.atan2(cross, dot));
    if (dTheta > 1e-9) minRadius = Math.min(minRadius, track.step / dTheta);
  }
  if (minRadius < 220) {
    warnings.push('Zone de départ très sinueuse — la grille sera en travers.');
  }
  return warnings;
}
